import type { WorkingSet } from '@floorfile/contracts'
import { OfflineDb, clearUserPartition, pruneTerminalOps, storeBlob } from './db'
import type { Codec } from './crypto'
import { compressImage } from './image'
import { isCacheable } from './manifests'
import { discardOp, enqueueOp, listOps, pendingCount, opsForEntity, rebaseOp, retryOp, type EnqueueInput } from './outbox'
import { ReachabilityMonitor } from './reachability'
import { SyncEngine, type SyncCallbacks } from './sync'
import type { NetState, OutboxOp, WorkingSetRow } from './types'
import { uuidv7 } from './uuid'

/**
 * The one per-app offline coordinator (doc §3): owns the Dexie store, the
 * reachability monitor and the sync engine; wires the sync triggers
 * (`online`, visibilitychange, a periodic timer while pending, manual
 * "Sync now" — Background Sync is deliberately NOT relied on, §3.1); and
 * scopes everything to the currently-attributed user (§9).
 */

/**
 * How long a terminal (failed/conflict) outbox op is kept for the worker to
 * retry or discard before it is aged out (audit RUN-16). Long enough to span
 * a holiday weekend plus a shift; short enough that abandoned ops cannot
 * accumulate into a full device.
 */
const TERMINAL_OP_RETENTION_MS = 30 * 24 * 60 * 60 * 1000

export interface OfflineRuntimeOptions {
  fetcher: SyncCallbacks['fetcher']
  onAuthRequired?: SyncCallbacks['onAuthRequired']
  onOpApplied?: SyncCallbacks['onOpApplied']
  /**
   * A DIRECT-path mutation (online, not queued) succeeded (FF-AUDIT-011).
   * The synced path already reports each applied op through `onOpApplied`;
   * without this the host could not refetch cross-module summaries after an
   * online write made through `useOfflineMutation`.
   */
  onDirectApplied?: () => void
  heartbeat?: () => Promise<boolean>
  dbName?: string
  /** Periodic drain cadence while ops are pending (ms, doc §5). */
  syncIntervalMs?: number
}

export class OfflineRuntime {
  readonly db: OfflineDb
  readonly engine: SyncEngine
  readonly reachability: ReachabilityMonitor
  /** The authenticated fetch — direct-path calls share it with the engine. */
  readonly fetch: SyncCallbacks['fetcher']
  /** See `OfflineRuntimeOptions.onDirectApplied`. */
  readonly onDirectApplied: () => void
  private currentUserId: string | undefined
  private currentCompanyId: string | undefined
  private listeners = new Set<() => void>()
  private timer: ReturnType<typeof setInterval> | undefined
  private opts: OfflineRuntimeOptions
  private visibilityHandler = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') this.requestSync()
  }
  private onlineHandler = () => this.requestSync()

  constructor(options: OfflineRuntimeOptions) {
    this.opts = options
    this.fetch = options.fetcher
    this.onDirectApplied = () => options.onDirectApplied?.()
    this.db = new OfflineDb(options.dbName)
    this.reachability = new ReachabilityMonitor({
      heartbeat:
        options.heartbeat ??
        (async () => {
          try {
            const controller = new AbortController()
            const timeout = setTimeout(() => controller.abort(), 4_000)
            const res = await fetch('/api/v1/health', { signal: controller.signal, cache: 'no-store' })
            clearTimeout(timeout)
            // Require a JSON answer: a same-origin SPA fallback returns the
            // 200 HTML shell for /api/* paths, which is NOT the API. Apps
            // whose API lives on another origin must pass their own
            // heartbeat targeting it.
            return res.ok && (res.headers.get('content-type') ?? '').includes('application/json')
          } catch {
            return false
          }
        }),
    })
    this.engine = new SyncEngine(this.db, {
      fetcher: options.fetcher,
      ...(options.onAuthRequired ? { onAuthRequired: options.onAuthRequired } : {}),
      ...(options.onOpApplied ? { onOpApplied: options.onOpApplied } : {}),
      onChange: () => this.emit(),
    })
    this.reachability.subscribe((state) => {
      if (state === 'online') this.requestSync()
      this.emit()
    })
  }

  start(): void {
    this.reachability.start()
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onlineHandler)
      document.addEventListener('visibilitychange', this.visibilityHandler)
    }
    this.timer = setInterval(() => {
      void (async () => {
        if (this.currentUserId && (await pendingCount(this.db, this.currentUserId)) > 0) {
          this.requestSync()
        }
      })()
    }, this.opts.syncIntervalMs ?? 30_000)
  }

  stop(): void {
    this.reachability.stop()
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandler)
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }
    if (this.timer) clearInterval(this.timer)
  }

  /** Attribute all local work to this user (doc §9). */
  setUser(user: { userId: string; companyId: string } | undefined): void {
    this.currentUserId = user?.userId
    this.currentCompanyId = user?.companyId
    this.emit()
    if (user) {
      // Age out long-dead terminal ops so the origin quota does not fill with
      // ballast on a shared tablet (audit RUN-16). Best-effort and
      // non-blocking: it must never delay the drain.
      void pruneTerminalOps(this.db, user.userId, TERMINAL_OP_RETENTION_MS)
        .then((pruned) => {
          if (pruned > 0) this.emit()
        })
        .catch(() => undefined)
      this.requestSync()
    }
  }

  /**
   * At-rest encryption control (§9): once a user unlocks their PIN, the app
   * hands the codec here and every subsequent sensitive write is encrypted /
   * read decrypted. Passing undefined re-locks (plaintext for a
   * non-encrypting user, or nothing readable for an encrypting one).
   */
  setCodec(codec: Codec | undefined): void {
    this.db.codec = codec
    this.emit()
  }

  get isEncrypted(): boolean {
    return this.db.codec !== undefined
  }

  get userId(): string | undefined {
    return this.currentUserId
  }

  get state(): NetState {
    return this.reachability.state
  }

  get isOffline(): boolean {
    return this.reachability.state === 'offline'
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private emit(): void {
    for (const listener of this.listeners) listener()
  }

  /** Queue a mutation for replay (doc §4 step 3). */
  async enqueue(input: Omit<EnqueueInput, 'userId' | 'companyId' | 'opId'> & { opId?: string }): Promise<OutboxOp> {
    if (!this.currentUserId || !this.currentCompanyId) {
      throw new Error('No attributed user — sign in before queueing offline work.')
    }
    if (!isCacheable(input.module)) {
      throw new Error(`${input.module} is declared never-cache (D24) and cannot queue offline.`)
    }
    const op = await enqueueOp(this.db, {
      ...input,
      opId: input.opId ?? uuidv7(),
      userId: this.currentUserId,
      companyId: this.currentCompanyId,
    })
    this.emit()
    this.requestSync()
    return op
  }

  /**
   * Capture a photo/signature offline (doc §6): compress, store the Blob,
   * queue the upload op. The returned attachment id is what the parent
   * op's payload references; the op id doubles as the dependency handle.
   */
  async captureAttachment(input: {
    blob: Blob
    fileName: string
    module: string
    targetEntityType: string
    targetEntityId: string
    /** F5.2: captured-in context and the original of an annotated copy — both
     * travel with the op, or an offline photo syncs as a context-less file and
     * the evidence gallery cannot place it. */
    context?: string | undefined
    derivedFromAttachmentId?: string | undefined
  }): Promise<{ attachmentId: string; opId: string }> {
    if (!this.currentUserId || !this.currentCompanyId) {
      throw new Error('No attributed user — sign in before capturing offline.')
    }
    // Check the D24 never-cache declaration BEFORE writing any bytes (audit
    // RUN-15). `enqueue` also enforces it, but it ran only AFTER `storeBlob`,
    // so a never-cache module's bytes (the HR/PII class) landed in IndexedDB
    // and stayed there — and the orphaned blob leaked, since only
    // removeOp/discardOp delete blobs.
    if (!isCacheable(input.module)) {
      throw new Error(`${input.module} is declared never-cache (D24) and cannot queue offline.`)
    }
    const attachmentId = uuidv7()
    const compressed = await compressImage(input.blob)
    await storeBlob(this.db, attachmentId, this.currentUserId, compressed)
    try {
      const op = await this.enqueue({
        opId: attachmentId,
        module: input.module,
        label: `Upload ${input.fileName}`,
        labelKey: 'attachments.upload',
        labelValues: { fileName: input.fileName },
        kind: 'attachment-upload',
        endpoint: '/api/v1/attachments',
        entityType: 'attachment',
        entityId: attachmentId,
        attachment: {
          fileName: input.fileName,
          mimeType: compressed.type || 'application/octet-stream',
          module: input.module,
          targetEntityType: input.targetEntityType,
          targetEntityId: input.targetEntityId,
          ...(input.context ? { context: input.context } : {}),
          ...(input.derivedFromAttachmentId ? { derivedFromAttachmentId: input.derivedFromAttachmentId } : {}),
        },
      })
      return { attachmentId, opId: op.opId }
    } catch (err) {
      // Store-then-enqueue is not atomic (no Dexie transaction spans them), so
      // roll the bytes back rather than leaking an orphan blob no code path
      // would ever delete (audit RUN-15).
      await this.db.blobs.delete(attachmentId).catch(() => undefined)
      throw err
    }
  }

  private syncScheduled = false

  /** Debounced drain — safe to call from every trigger. */
  requestSync(): void {
    if (this.syncScheduled || !this.currentUserId) return
    this.syncScheduled = true
    setTimeout(() => {
      this.syncScheduled = false
      const userId = this.currentUserId
      if (!userId || this.reachability.state === 'offline') return
      // Audit RUN-20: `.then` with no `.catch` — a Dexie failure killed the
      // whole pass as an unhandled rejection, silently. A drain that cannot
      // run must leave the outbox intact and say so, not vanish.
      void this.engine
        .drain(userId)
        .then((result) => {
          if (result.applied > 0) this.reachability.noteSuccess()
        })
        .catch((err: unknown) => {
          console.error('[offline] drain failed', err)
        })
    }, 250)
  }

  async outboxOps(): Promise<OutboxOp[]> {
    if (!this.currentUserId) return []
    return listOps(this.db, this.currentUserId)
  }

  /**
   * The sync center's three verbs, owned here so the change is BROADCAST.
   * They used to be called straight on the outbox table: the row went, but
   * nothing told `useOutbox`, so the failed item stayed on screen and the
   * indicator kept saying "Sync issue" until some unrelated drain happened
   * to emit — a person who pressed Discard saw nothing change and concluded
   * the error could not be cleared (owner report, 2026-09-14).
   */
  async discard(opId: string): Promise<void> {
    await discardOp(this.db, opId)
    this.emit()
  }

  async retry(opId: string): Promise<void> {
    await retryOp(this.db, opId)
    this.emit()
    this.requestSync()
  }

  async rebase(opId: string, input: { baseVersion: number; payload?: unknown }): Promise<void> {
    await rebaseOp(this.db, opId, input)
    this.emit()
    this.requestSync()
  }

  async entityOps(entityId: string): Promise<OutboxOp[]> {
    if (!this.currentUserId) return []
    return opsForEntity(this.db, this.currentUserId, entityId)
  }

  // ── drafts (doc §3 "drafts autosave continuously") ────────────────────
  async saveDraft(key: string, values: unknown): Promise<void> {
    if (!this.currentUserId) return
    const codec = this.db.codec
    await this.db.drafts.put({
      key: `${this.currentUserId}:${key}`,
      userId: this.currentUserId,
      values: codec ? { __enc: await codec.encrypt(JSON.stringify(values ?? null)) } : values,
      updatedAt: new Date().toISOString(),
    })
  }

  async readDraft<T>(key: string): Promise<T | undefined> {
    if (!this.currentUserId) return undefined
    const row = await this.db.drafts.get(`${this.currentUserId}:${key}`)
    if (!row) return undefined
    const enc = (row.values as { __enc?: string } | null)?.__enc
    if (typeof enc === 'string') {
      if (!this.db.codec) return undefined
      try {
        return JSON.parse(await this.db.codec.decrypt(enc)) as T
      } catch {
        return undefined
      }
    }
    return row.values as T | undefined
  }

  async clearDraft(key: string): Promise<void> {
    if (!this.currentUserId) return
    await this.db.drafts.delete(`${this.currentUserId}:${key}`)
  }

  // ── the cached working set (doc §3/§14) ───────────────────────────────
  async saveWorkingSet(workingSet: WorkingSet): Promise<void> {
    if (!this.currentUserId) return
    const entries = workingSet.entries.filter((e) => isCacheable(e.module))
    const codec = this.db.codec
    const plain = { entries, identifiers: workingSet.identifiers, counts: workingSet.counts }
    await this.db.workingSet.put({
      userId: this.currentUserId,
      generatedAt: workingSet.generatedAt,
      ...(codec
        ? { entries: [], identifiers: [], counts: {}, enc: await codec.encrypt(JSON.stringify(plain)) }
        : plain),
    })
    this.emit()
  }

  async getWorkingSet(): Promise<WorkingSetRow | undefined> {
    if (!this.currentUserId) return undefined
    const row = await this.db.workingSet.get(this.currentUserId)
    if (!row?.enc) return row
    if (!this.db.codec) return undefined
    try {
      const plain = JSON.parse(await this.db.codec.decrypt(row.enc)) as Pick<
        WorkingSetRow,
        'entries' | 'identifiers' | 'counts'
      >
      return { userId: row.userId, generatedAt: row.generatedAt, ...plain }
    } catch {
      return undefined
    }
  }

  /** Explicit logout: clear the user's cached data; queued work stays (§9). */
  async clearPartition(options?: { dropOutbox?: boolean }): Promise<void> {
    if (!this.currentUserId) return
    await clearUserPartition(this.db, this.currentUserId, options)
    this.emit()
  }
}
